import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import SeverityBadge from "@/components/shared/SeverityBadge";
import StatusBadge from "@/components/shared/StatusBadge";
import MapView from "@/components/shared/MapView";
import { useAuth } from "@/lib/auth";
import api, { extractApiError } from "@/lib/api";
import { downloadReport } from "@/lib/downloadReport";
import { toast } from "sonner";
import { Check, Loader2, RefreshCw, Send, FileText } from "lucide-react";
import LocationLabel from "@/components/shared/LocationLabel";

const VIEW_MODES = {
  ACTIVE: "active",
  TRIAGE: "triage",
};

const FILTER_ALL = "ALL";

const SERVICE_RESPONSE_STATUSES = [
  "NOTIFIED",
  "ACKNOWLEDGED",
  "DISPATCHED",
  "EN_ROUTE",
  "ON_SCENE",
  "RESOLVED",
  "UNABLE_TO_RESPOND",
];

const STATUS_ACTIONS = {
  OPEN: "ACKNOWLEDGED",
  ACKNOWLEDGED: "RESPONDING",
};

const STATUS_LABELS = {
  OPEN: "Acknowledge",
  ACKNOWLEDGED: "Mark Responding",
};

const AVAILABLE_VEHICLE_STATUSES = new Set(["AVAILABLE", "IDLE", "READY", "ONLINE"]);

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function toText(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") {
    if (("lat" in value || "latitude" in value) && ("lon" in value || "lng" in value || "longitude" in value)) {
      const lat = value.lat ?? value.latitude;
      const lon = value.lon ?? value.lng ?? value.longitude;
      return `${lat}, ${lon}`;
    }
    if (typeof value.address === "string") return value.address;
    if (typeof value.place === "string") return value.place;
    return JSON.stringify(value);
  }
  return String(value);
}

function parseCoords(item) {
  if (!item) return null;
  if (Array.isArray(item.coords) && item.coords.length === 2) return item.coords;
  if (Array.isArray(item.location_coords) && item.location_coords.length === 2) return item.location_coords;
  const location = item.location;
  if (location && typeof location === "object") {
    const lat = Number.parseFloat(location.lat ?? location.latitude);
    const lng = Number.parseFloat(location.lon ?? location.lng ?? location.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }
  const lat = Number.parseFloat(item.latitude ?? item.lat ?? item.lat_deg ?? item.latitude_deg);
  const lng = Number.parseFloat(item.longitude ?? item.lng ?? item.lon ?? item.longitude_deg);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  return null;
}

function normalizeAssignedUnits(item) {
  if (!Array.isArray(item?.assigned_units)) return [];

  return item.assigned_units
    .map((unit) => {
      if (typeof unit === "string" || typeof unit === "number") {
        const text = String(unit);
        return { id: text, label: text };
      }

      if (!unit || typeof unit !== "object") return null;

      const id = unit.id || unit.vehicle_id || unit.unit_id || "";
      const label =
        unit.registration_number
        || unit.registration
        || unit.plate
        || unit.vehicle_label
        || unit.vehicle_name
        || unit.call_sign
        || (id ? String(id) : "");

      if (!id && !label) return null;
      return {
        id: id ? String(id) : String(label),
        label: String(label || id),
      };
    })
    .filter((unit) => unit?.id && unit?.label);
}

function normalizeVehicleOption(item) {
  const id = item?.id || item?.vehicle_id;
  if (!id) return null;

  const status = String(item?.computed_status || item?.status || item?.state || "").toUpperCase();
  const label =
    item?.registration_number
    || item?.plate
    || item?.vehicle_name
    || item?.name
    || String(id);

  return {
    id: String(id),
    label: String(label),
    status,
  };
}

function normalizeIncident(item) {
  const status = String(item?.status || item?.current_status || "open").toUpperCase();
  const severity = String(item?.severity || item?.priority || "low").toLowerCase();
  const assignedUnits = normalizeAssignedUnits(item);
  
  // Extract vehicle info from vehicles array or fallback to flat fields
  let vehicle = "—";
  if (assignedUnits.length > 0) {
    vehicle = assignedUnits.map((unit) => unit.label).join(", ");
  } else if (Array.isArray(item?.vehicles) && item.vehicles.length > 0) {
    const primaryVehicle = item.vehicles.find((v) => v?.role === "PRIMARY") || item.vehicles[0];
    vehicle = toText(primaryVehicle?.registration_number || primaryVehicle?.vehicle_id || "—");
  } else {
    vehicle = toText(item?.vehicle || item?.vehicle_plate || item?.plate || "—");
  }
  
  // Extract driver info from driver object or fallback to flat fields
  let driver = "—";
  if (item?.driver && typeof item.driver === "object") {
    driver = toText(item.driver.name || item.driver.user_id || "—");
  } else {
    driver = toText(item?.driver || item?.operator || "—");
  }
  
  // Use incident_type if available, otherwise fallback to type/category
  const incidentType = item?.incident_type || item?.type || item?.category || item?.summary || "Incident";
  
  return {
    id: item?.id || item?.incident_id || "",
    type: toText(incidentType),
    location: toText(item?.location || item?.address || item?.place || "—"),
    locationRaw: item?.location ?? item?.address ?? item?.place ?? null,
    severity,
    status,
    vehicle,
    assignedUnits,
    driver,
    eta: toText(item?.eta || item?.eta_minutes || item?.estimated_arrival || "—"),
    notes: toText(item?.description || item?.notes || item?.reviewer_notes || ""),
    coords: parseCoords(item),
    updatedAt: item?.updated_at || item?.modified_at || item?.created_at || item?.timestamp || item?.event_time || "",
    raw: item,
  };
}

function normalizeServiceResponse(item) {
  return {
    id: item?.id || item?.response_id || item?.audit_id || "",
    status: String(item?.status || "UNKNOWN").toUpperCase(),
    note: item?.note || item?.notes || item?.description || "",
    orgType: item?.org_type || item?.organization_type || "POLICE",
    createdAt: item?.created_at || item?.timestamp || item?.updated_at || "",
  };
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function toIsoDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString();
}

export default function PoliceActiveCases() {
  const { can, orgId } = useAuth();
  const canUpdateIncidents = can("INCIDENTS.UPDATE");
  const canDispatchIncidents = can("INCIDENTS.DISPATCH");
  const [viewMode, setViewMode] = useState(VIEW_MODES.ACTIVE);
  const [severityFilter, setSeverityFilter] = useState(FILTER_ALL);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCase, setSelectedCase] = useState(null);
  const [incidentDetail, setIncidentDetail] = useState(null);
  const [serviceResponses, setServiceResponses] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [responseSubmitting, setResponseSubmitting] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [serviceStatus, setServiceStatus] = useState(SERVICE_RESPONSE_STATUSES[0]);
  const [serviceNote, setServiceNote] = useState("");
  const [advanceNote, setAdvanceNote] = useState("");
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [selectedAssignedVehicleId, setSelectedAssignedVehicleId] = useState("");

  const canAdvance = selectedCase && STATUS_ACTIONS[selectedCase.status];
  const nextStatus = selectedCase ? STATUS_ACTIONS[selectedCase.status] : null;
  const nextStatusLabel = selectedCase ? STATUS_LABELS[selectedCase.status] : "Update status";
  // Vehicle assignment now belongs to the service-response dialog (POST path), not the PATCH path.
  const requiresVehicleAssignment = serviceStatus === "DISPATCHED";

  const fetchAvailableVehicles = useCallback(async () => {
    setLoadingVehicles(true);

    try {
      const params = { is_active: true, page_size: 100 };
      if (orgId) params.org_id = orgId;

      const { data } = await api.get("/vehicles", { params });
      const vehicles = normalizeCollection(data)
        .map(normalizeVehicleOption)
        .filter((vehicle) => vehicle?.id)
        .filter((vehicle) => !vehicle.status || AVAILABLE_VEHICLE_STATUSES.has(vehicle.status))
        .sort((a, b) => a.label.localeCompare(b.label));

      setAvailableVehicles(vehicles);
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load available vehicles.");
      setAvailableVehicles([]);
    } finally {
      setLoadingVehicles(false);
    }
  }, [orgId]);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      let list = [];
      if (viewMode === VIEW_MODES.ACTIVE) {
        const [ackRes, respRes] = await Promise.all([
          api.get("/incidents", { params: { status: "ACKNOWLEDGED", page_size: 50 } }),
          api.get("/incidents", { params: { status: "RESPONDING", page_size: 50 } }),
        ]);

        const acknowledged = normalizeCollection(ackRes.data).map(normalizeIncident);
        const responding = normalizeCollection(respRes.data).map(normalizeIncident);
        list = [...acknowledged, ...responding];
      } else {
        const params = { status: "OPEN", page_size: 50 };
        if (severityFilter !== FILTER_ALL) params.severity = severityFilter;

        const { data } = await api.get("/incidents", { params });
        list = normalizeCollection(data).map(normalizeIncident);
      }

      const sorted = [...list].sort((a, b) => {
        const aTime = new Date(a.updatedAt).getTime() || 0;
        const bTime = new Date(b.updatedAt).getTime() || 0;
        return bTime - aTime;
      });

      setCases(sorted);
      setSelectedCase((current) => {
        if (!current) return sorted[0] || null;
        const found = sorted.find((item) => item.id === current.id);
        return found || sorted[0] || null;
      });
    } catch (err) {
      setError(extractApiError(err).message || "Failed to load active cases.");
      setCases([]);
      setSelectedCase(null);
    } finally {
      setLoading(false);
    }
  }, [viewMode, severityFilter]);

  const fetchCaseDetail = useCallback(async (incidentId) => {
    if (!incidentId) return;
    setDetailLoading(true);
    setIncidentDetail(null);
    setServiceResponses([]);

    try {
      const { data: detailData } = await api.get(`/incidents/${incidentId}`);
      const rawDetail = detailData?.data || detailData || {};
      
      const normalizedDetail = normalizeIncident(rawDetail);
      setIncidentDetail(rawDetail);
      setSelectedCase(normalizedDetail);
      
      // Extract service statuses from incident payload (keyed by org type: POLICE, AMBULANCE, FIRE, etc.)
      const serviceStatuses = rawDetail?.service_statuses || {};
      const responses = Object.entries(serviceStatuses).map(([orgType, statusData]) => ({
        id: `${orgType}-${rawDetail.id}`,
        status: String(statusData?.status || "UNKNOWN").toUpperCase(),
        note: statusData?.note || statusData?.notes || statusData?.description || "",
        orgType: orgType,
        createdAt: statusData?.updated_at || statusData?.created_at || statusData?.timestamp || "",
      }));
      
      // Also fetch service-responses endpoint if it exists for additional audit entries
      try {
        const { data: responsesData } = await api.get(`/incidents/${incidentId}/service-responses`, { params: { page: 1, page_size: 100 } });
        const auditEntries = normalizeCollection(responsesData)
          .map(normalizeServiceResponse);
        setServiceResponses([...responses, ...auditEntries].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      } catch {
        // If service-responses endpoint fails, use only service_statuses
        setServiceResponses(responses.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      }
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load incident details.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Auto-default Triage severity when switching to TRIAGE mode
  useEffect(() => {
    if (viewMode === VIEW_MODES.TRIAGE) {
      setSeverityFilter("CRITICAL");
    }
  }, [viewMode]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  useEffect(() => {
    if (selectedCase?.id) {
      fetchCaseDetail(selectedCase.id);
    }
  }, [selectedCase?.id, fetchCaseDetail]);

  useEffect(() => {
    if (!requiresVehicleAssignment || !selectedCase?.id) {
      setSelectedAssignedVehicleId("");
      return;
    }

    fetchAvailableVehicles();
  }, [fetchAvailableVehicles, requiresVehicleAssignment, selectedCase?.id]);

  const handleRefresh = async () => {
    await fetchCases();
    if (selectedCase?.id) {
      await fetchCaseDetail(selectedCase.id);
    }
  };

  const handleAdvanceStatus = async () => {
    if (!canUpdateIncidents) {
      toast.error("You do not have permission to update incident status.");
      return;
    }
    if (!selectedCase?.id || !nextStatus) return;
    setStatusSubmitting(true);

    try {
      const payload = { status: nextStatus };
      if (advanceNote.trim()) {
        payload.description = advanceNote.trim();
      }
      await api.patch(`/incidents/${selectedCase.id}/status`, payload);
      toast.success(`Incident ${selectedCase.id} updated to ${nextStatus}.`);
      setAdvanceNote("");
      await handleRefresh();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to update incident status.");
    } finally {
      setStatusSubmitting(false);
    }
  };

  const openServiceDialog = () => {
    if (!canDispatchIncidents) {
      toast.error("You do not have permission to log service responses.");
      return;
    }
    setServiceStatus(SERVICE_RESPONSE_STATUSES[0]);
    setServiceNote("");
    setServiceDialogOpen(true);
  };

  const submitServiceResponse = async () => {
    if (!canDispatchIncidents) {
      toast.error("You do not have permission to log service responses.");
      return;
    }
    if (!selectedCase?.id) return;
    setResponseSubmitting(true);

    try {
      const payload = { status: serviceStatus };
      if (serviceNote.trim()) {
        payload.note = serviceNote.trim();
      }
      // assigned_vehicle_id is only accepted by PATCH /incidents/{id}/status (not service-response).
      await api.post(`/incidents/${selectedCase.id}/service-response`, payload);
      if (selectedAssignedVehicleId) {
        await api.patch(`/incidents/${selectedCase.id}/status`, {
          assigned_vehicle_id: selectedAssignedVehicleId,
        });
      }
      toast.success(`Service response logged: ${serviceStatus}.`);
      setServiceDialogOpen(false);
      await fetchCaseDetail(selectedCase.id);
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to log service response.");
    } finally {
      setResponseSubmitting(false);
    }
  };

  const selectedDetail = incidentDetail || selectedCase?.raw || {};

  const reportRows = cases.map((c) => [c.id, c.type, c.location, c.severity, c.status, c.vehicle]);

  return (
    <div className="space-y-6" data-testid="police-active-cases">
      <PageHeader
        title="Active Cases"
        subtitle="Acknowledged, responding and triage incidents from the incidents API."
        actions={(
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={handleRefresh} disabled={loading || statusSubmitting || responseSubmitting}>
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </Button>
          </div>
        )}
        onDownload={() => {
          downloadReport({
            filename: "police_active_cases",
            title: "Police - Active Cases Report",
            headers: ["Case", "Type", "Location", "Severity", "Status", "Vehicle"],
            rows: reportRows,
          });
          toast.success("Report downloaded");
        }}
      />

      <SectionCard>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant={viewMode === VIEW_MODES.ACTIVE ? "default" : "outline"}
            onClick={() => setViewMode(VIEW_MODES.ACTIVE)}
          >
            Active cases
          </Button>
          <Button
            size="sm"
            variant={viewMode === VIEW_MODES.TRIAGE ? "default" : "outline"}
            onClick={() => setViewMode(VIEW_MODES.TRIAGE)}
          >
            Triage view
          </Button>
        </div>

        {viewMode === VIEW_MODES.TRIAGE ? (
          <div className="mt-4 flex items-center gap-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Severity</Label>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="h-10 min-w-[160px]" aria-label="Severity filter">
                <SelectValue placeholder="All severities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FILTER_ALL}>All severities</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </SectionCard>

      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-3 xl:col-span-1">
          <SectionCard>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{viewMode === VIEW_MODES.ACTIVE ? "Active incidents" : "Open triage incidents"}</p>
                <p className="text-xs text-slate-500">{loading ? "Loading cases..." : `${cases.length} case${cases.length === 1 ? "" : "s"}`}</p>
              </div>
              <StatusBadge status={viewMode === VIEW_MODES.ACTIVE ? "acknowledged" : "open"} />
            </div>
          </SectionCard>
          {cases.length === 0 ? (
            <SectionCard>
              <p className="text-sm text-slate-500">No incidents match the selected filters.</p>
            </SectionCard>
          ) : cases.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedCase(item)}
              className={`w-full rounded-2xl border bg-white p-4 text-left transition ${selectedCase?.id === item.id ? "border-[hsl(var(--role-primary))] shadow-md" : "border-slate-100 hover:border-slate-200"}`}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold text-slate-900">{item.type}</p>
                <SeverityBadge severity={item.severity} />
              </div>
              <LocationLabel locationValue={item.locationRaw ?? item.location} className="mt-3 text-xs text-slate-500" />
              <div className="mt-3 flex items-center justify-between gap-3 text-xs">
                <StatusBadge status={item.status} />
                <span className="text-slate-500">{formatDateTime(item.updatedAt)}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="space-y-4 xl:col-span-2">
          {selectedCase ? (
            <>
              <SectionCard padded={false}>
                {selectedCase.coords ? (
                  <div className="p-3">
                    <MapView
                      markers={[{ coords: selectedCase.coords, title: selectedCase.type, subtitle: selectedCase.location, severity: selectedCase.severity }]}
                      center={selectedCase.coords}
                      zoom={14}
                      height={340}
                      testId="police-active-map"
                    />
                  </div>
                ) : (
                  <div className="p-6 text-center text-sm text-slate-500">Location coordinates unavailable for this incident.</div>
                )}
              </SectionCard>

              <SectionCard title={selectedCase.type}>
                <div className="grid gap-3 sm:grid-cols-2 text-sm">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Status</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900"><StatusBadge status={selectedCase.status} /></p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Severity</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900"><SeverityBadge severity={selectedCase.severity} /></p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Assigned Units</p>
                    <p className="mt-1 font-mono text-sm font-semibold text-slate-900">{selectedCase.vehicle}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Updated</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{formatDateTime(selectedCase.updatedAt)}</p>
                  </div>

                  {(selectedDetail?.resolved_at || selectedDetail?.closed_at) && (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Resolved</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{formatDateTime(selectedDetail?.resolved_at || selectedDetail?.closed_at)}</p>
                    </div>
                  )}
                </div>

                <div className="mt-5 space-y-4">
                  <div className="space-y-2">
                    <Label>Reviewer notes (optional)</Label>
                    <Textarea
                      value={advanceNote}
                      onChange={(event) => setAdvanceNote(event.target.value)}
                      placeholder="Add a note for the status update..."
                      rows={3}
                    />
                  </div>
                  {null /* vehicle assignment moved to the service-response dialog */}
                  <div className="flex flex-wrap gap-2">
                    {canUpdateIncidents ? (
                      <Button
                        onClick={handleAdvanceStatus}
                        disabled={!canAdvance || statusSubmitting}
                        className="gap-2"
                      >
                        {statusSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        {nextStatusLabel}
                      </Button>
                    ) : null}
                    {canDispatchIncidents ? (
                      <Button variant="outline" onClick={openServiceDialog} disabled={responseSubmitting} className="gap-2">
                        <FileText size={14} /> Log service update
                      </Button>
                    ) : null}
                  </div>
                </div>
              </SectionCard>

            </>
          ) : (
            <SectionCard>
              <p className="text-sm text-slate-500">Select an incident to view details and log updates.</p>
            </SectionCard>
          )}
        </div>
      </div>

      <Dialog open={serviceDialogOpen} onOpenChange={(open) => !responseSubmitting && setServiceDialogOpen(open)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Service Response</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={serviceStatus} onValueChange={setServiceStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_RESPONSE_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Note (Optional)</Label>
              <Textarea
                value={serviceNote}
                onChange={(event) => setServiceNote(event.target.value)}
                placeholder="e.g. Unit POL-04 dispatched from central precinct"
                rows={4}
              />
            </div>

            {requiresVehicleAssignment ? (
              <div className="space-y-2">
                <Label>Assign vehicle (optional)</Label>
                <Select
                  value={selectedAssignedVehicleId || "NONE"}
                  onValueChange={(value) => setSelectedAssignedVehicleId(value === "NONE" ? "" : value)}
                >
                  <SelectTrigger aria-label="Assign vehicle">
                    <SelectValue placeholder={loadingVehicles ? "Loading fleet vehicles..." : "No assignment"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">No assignment</SelectItem>
                    {availableVehicles.map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>{vehicle.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {loadingVehicles ? "Fetching available vehicles from your fleet..." : `${availableVehicles.length} available vehicle${availableVehicles.length === 1 ? "" : "s"} in your fleet.`}
                </p>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setServiceDialogOpen(false)} disabled={responseSubmitting}>Cancel</Button>
            {canDispatchIncidents ? (
              <Button onClick={submitServiceResponse} disabled={responseSubmitting} className="gap-2">
                {responseSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Log update
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
